import clsx from 'clsx'
import {getSubscriptionSignUpUrl} from 'common/HelperFunctions'
import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {Link} from 'react-router'
import * as Yup from 'yup'

import {AiLoginErrorAlert} from '../components/AiLoginErrorAlert'
import {SelectiveAiGoogleLogin} from '../components/SelectiveAiGoogleLogin'
import {useUniversalLogin} from '../hooks/useUniversalLogin'
import {UniversalLoginSchoolPicker} from '../modals/UniversalLoginSchoolPicker'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter a valid email address')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const inputStyle = {
  background: 'rgba(55, 35, 35, 0.8)',
  border: 'none',
  borderRadius: 10,
  padding: '13px 16px',
  color: 'white',
  fontSize: 15,
}

const buttonStyle = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: 10,
  padding: '12px 28px',
  fontSize: 15,
  fontWeight: 600,
}

export function UniversalLoginPage() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const {
    showSchoolPicker,
    schoolInstances,
    pickerLoading,
    aiLoginError,
    emailLoginOnly,
    showEmailLoginForm,
    clearAiLoginError,
    submitUniversalLogin,
    handleSchoolSelect,
    closeSchoolPicker,
  } = useUniversalLogin()

  const formik = useFormik({
    initialValues: {email: '', password: ''},
    validationSchema: loginSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setSubmitting(true)
      clearAiLoginError()
      setStatus('')
      submitUniversalLogin(values.email, values.password, {
        setStatus,
        setSubmitting,
        onLoadingChange: setLoading,
      })
    },
  })

  const handleSignInWithEmail = (email?: string) => {
    const prefilled = showEmailLoginForm(email)
    if (prefilled) {
      formik.setFieldValue('email', prefilled)
    }
  }

  useEffect(() => {
    if (emailLoginOnly && aiLoginError?.email) {
      formik.setFieldValue('email', aiLoginError.email)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailLoginOnly, aiLoginError?.email])

  return (
    <>
      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
        <div className='text-center mb-10'>
          <h1 className='mb-3 text-white fw-bolder'>Sign in</h1>
          <div className='fs-6 text-white opacity-75'>
            Sign in to{' '}
            <a
              href='https://da1timetable.ai'
              target='_blank'
              rel='noreferrer'
              className='text-primary text-decoration-underline'
            >
              Da1Timetable
            </a>
          </div>
        </div>

        {aiLoginError ? (
          <AiLoginErrorAlert error={aiLoginError} onSignInWithEmail={handleSignInWithEmail} />
        ) : formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : null}

        <div className='fv-row mb-10'>
          <label className='form-label fs-6 fw-bolder text-white'>
            Email <span style={{color: '#ef4444'}}>*</span>
          </label>
          <input
            {...formik.getFieldProps('email')}
            className={clsx('form-control form-control-lg', {
              'is-invalid': formik.touched.email && formik.errors.email,
            })}
            style={inputStyle}
            type='email'
            placeholder='Email'
            autoFocus
            autoComplete='email'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>

        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-between mt-n5'>
            <div className='d-flex flex-stack mb-2 w-100'>
              <label className='form-label fw-bolder fs-6 mb-0 text-white'>
                Password <span style={{color: '#ef4444'}}>*</span>
              </label>
              <Link
                to='/auth/forgot-password'
                className='link-primary fs-6 fw-bolder text-decoration-underline'
              >
                Forgot / Set Password?
              </Link>
            </div>
          </div>
          <div className='position-relative'>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              {...formik.getFieldProps('password')}
              className={clsx('form-control form-control-lg', {
                'is-invalid': formik.touched.password && formik.errors.password,
              })}
              style={{...inputStyle, paddingRight: '4.5rem'}}
              placeholder='Password'
            />
            <div
              className={clsx(
                'position-absolute end-0 top-50 translate-middle-y text-white',
                formik.touched.password && formik.errors.password ? 'me-12' : 'me-5'
              )}
            >
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='universal-login-show-password'>
                    {showPassword ? 'Hide' : 'Show'} Password
                  </Tooltip>
                }
              >
                <i
                  className={clsx('cursor-pointer fa', {
                    'fa-eye': !showPassword,
                    'fa-eye-slash': showPassword,
                  })}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-hidden='true'
                  role='button'
                />
              </OverlayTrigger>
            </div>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>

        <div className='text-center'>
          <button
            type='submit'
            className='btn btn-lg w-100 mb-5 rounded'
            style={buttonStyle}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              !formik.values.email?.trim() ||
              !formik.values.password
            }
          >
            {!loading && <span className='indicator-label'>Login</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            )}
          </button>
        </div>

        {!emailLoginOnly ? <SelectiveAiGoogleLogin disabled={loading} isSpecialDomain /> : null}
      </form>

      <div className='text-center mt-8'>
        <span className='text-white-50 fw-semibold fs-6'>Don&apos;t have an account? </span>
        <a
          href={getSubscriptionSignUpUrl()}
          className='link-primary fs-6 fw-bolder text-decoration-underline'
        >
          Sign Up
        </a>
      </div>

      <UniversalLoginSchoolPicker
        show={showSchoolPicker}
        instances={schoolInstances}
        loading={pickerLoading}
        onSelect={(instance) =>
          handleSchoolSelect(instance, {
            setStatus: formik.setStatus,
            setSubmitting: formik.setSubmitting,
            onLoadingChange: setLoading,
          })
        }
        onClose={closeSchoolPicker}
      />
    </>
  )
}
